import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  useDisclosure,
  Button,
  GridItem,
  Text,
  Flex,
  Container,
  Spinner,
  AspectRatio,
  Box,
} from '@chakra-ui/react';
import ReactPlayer from 'react-player';
import MediaBackground from '@repo/ui-components/dept/molecules/MediaBackground';
import CTAButton from '@repo/ui-components/tailwind/CTAButton';
import { generateSectionId } from '@repo/ui-components/dept/utils/generateSectionId';
import Image from '@repo/ui-components/tailwind/Image';
import DrawerModal from '@repo/ui-components/dept/molecules/DrawerModal';
import { isLinkValid } from '@repo/gatsby-localization-helpers';

function ImageHeroesBold({ block }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { headline, copy, cta, background } = block;
  const [isLoading, setIsLoading] = useState(true);

  const videoUrl = cta?.link?.href;

  useEffect(() => {
    setIsLoading(true);
  }, [onOpen]);

  return (
    <Container {...generateSectionId(block)}>
      <MediaBackground
        // block.media is what the property will be called when this is included in a reusable block and the properties come through raw
        background={background ?? block.media}
        mb={6}
        minH={{ base: '28.56rem', lg: '38.62rem' }}
      >
        <GridItem
          as={Flex}
          colSpan={12}
          rowSpan={1}
          h="full"
          direction="column"
          justify="end"
          align="center"
          textAlign="center"
          zIndex={1}
          gap={6}
          p={{ base: 6, md: 16 }}
          pb={{ base: 10, md: null }}
        >
          {copy && (
            <Text textStyle="h3" color="typography.lighter" fontWeight="bold">
              {copy}
            </Text>
          )}
          {headline && (
            <Text
              as="h1"
              textStyle="display"
              color="typography.lighter"
              mb={2}
              lineHeight="1"
            >
              {headline}
            </Text>
          )}

          {isLinkValid(cta?.link) &&
            (background.mediaType === 'video' ? (
              <Button onClick={onOpen}>
                {cta.icon && (
                  <Image
                    className="fill-white"
                    sanityImage={cta.icon}
                    width="1.6rem"
                  />
                )}
                {cta.text}
              </Button>
            ) : (
              <CTAButton isExternal cta={cta} />
            ))}
        </GridItem>
      </MediaBackground>
      <DrawerModal isOpen={isOpen} onClose={onClose} modalWidth="62.5rem">
        <AspectRatio w="100%" ratio={16 / 9} mt={[8, null, 0]}>
          <>
            {isLoading && (
              <Box>
                <Spinner size="lg" color="typography.light" />
              </Box>
            )}
            <ReactPlayer
              opacity={isLoading ? '0' : '1'}
              url={videoUrl}
              width="100%"
              height="100%"
              style={{
                borderRadius: '8px',
              }}
              onReady={() => setIsLoading(false)}
              onBufferEnd={() => setIsLoading(false)}
              onBuffer={() => setIsLoading(true)}
            />
          </>
        </AspectRatio>
      </DrawerModal>
    </Container>
  );
}

ImageHeroesBold.propTypes = {
  block: PropTypes.shape({
    headline: PropTypes.string,
    copy: PropTypes.string,
    background: PropTypes.shape({
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  }),
  cta: PropTypes.shape({
    text: PropTypes.string,
    link: PropTypes.shape({
      href: PropTypes.string,
      linkType: PropTypes.string,
      internalLink: PropTypes.shape({
        slug: PropTypes.shape({
          current: PropTypes.string,
        }),
      }),
    }),
    icon: PropTypes.shape({
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  }),
}.isRequired;

export default ImageHeroesBold;
